"use client";

import { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { CREDIT_PACKAGES, formatBRL, type CreditPackage } from "@/lib/stripe";

// ── Success/Cancel banners ─────────────────────────────────────────────────

function StatusBanner() {
  const params = useSearchParams();
  const success = params.get("success");
  const cancelled = params.get("cancelled");
  const pkg = params.get("package");
  const credits = params.get("credits");

  if (success) {
    return (
      <div style={{
        background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)",
        borderRadius: 12, padding: "1rem 1.5rem", marginBottom: "2rem",
        color: "#6ee7b7", display: "flex", alignItems: "center", gap: "0.75rem",
        fontSize: "0.9rem",
      }}>
        <span style={{ fontSize: "1.5rem" }}>🎉</span>
        <div>
          <div style={{ fontWeight: 700 }}>Pagamento confirmado!</div>
          <div style={{ opacity: 0.8, fontSize: "0.8rem" }}>
            {credits} créditos do pacote <strong>{pkg}</strong> foram adicionados à sua conta.
          </div>
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div style={{
        background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)",
        borderRadius: 12, padding: "1rem 1.5rem", marginBottom: "2rem",
        color: "#fcd34d", fontSize: "0.85rem",
      }}>
        ⚠️ Pagamento cancelado. Você pode tentar novamente a qualquer momento.
      </div>
    );
  }

  return null;
}

// ── Package Card ───────────────────────────────────────────────────────────

function PackageCard({
  pkg,
  onSelect,
  loading,
}: {
  pkg: CreditPackage;
  onSelect: (id: string) => void;
  loading: string | null;
}) {
  const isLoading = loading === pkg.id;
  const pricePerCredit = (pkg.priceCents / pkg.credits / 100).toFixed(3);

  return (
    <div
      style={{
        position: "relative",
        background: pkg.popular
          ? "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(59,130,246,0.12))"
          : "rgba(16,16,32,0.8)",
        border: pkg.popular
          ? "1px solid rgba(124,58,237,0.4)"
          : "1px solid rgba(255,255,255,0.07)",
        borderRadius: "1.25rem",
        padding: "2rem",
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
        transition: "transform 0.2s, border-color 0.2s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = "translateY(-4px)";
        if (!pkg.popular) (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(124,58,237,0.3)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
        if (!pkg.popular) (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.07)";
      }}
    >
      {/* Popular badge */}
      {pkg.badge && (
        <div
          style={{
            position: "absolute",
            top: -12,
            left: "50%",
            transform: "translateX(-50%)",
            background: pkg.popular
              ? "linear-gradient(135deg, #7c3aed, #3b82f6)"
              : "rgba(52,211,153,0.15)",
            border: pkg.popular ? "none" : "1px solid rgba(52,211,153,0.3)",
            color: pkg.popular ? "white" : "#6ee7b7",
            borderRadius: 20,
            padding: "0.2rem 0.875rem",
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.05em",
            whiteSpace: "nowrap",
          }}
        >
          {pkg.badge}
        </div>
      )}

      {/* Header */}
      <div>
        <div style={{ fontSize: "0.75rem", color: "rgba(160,160,200,0.5)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.25rem" }}>
          Pacote
        </div>
        <div style={{ fontSize: "1.5rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
          {pkg.name}
        </div>
      </div>

      {/* Credits */}
      <div style={{
        background: "rgba(124,58,237,0.1)",
        border: "1px solid rgba(124,58,237,0.2)",
        borderRadius: 10,
        padding: "1rem",
        textAlign: "center",
      }}>
        <div style={{ fontSize: "3rem", fontWeight: 900, color: "#a78bfa", lineHeight: 1 }}>
          {pkg.credits}
        </div>
        <div style={{ fontSize: "0.75rem", color: "rgba(160,160,200,0.6)", marginTop: "0.25rem" }}>
          créditos de geração
        </div>
      </div>

      {/* Price */}
      <div>
        <div style={{ fontSize: "2rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
          {formatBRL(pkg.priceCents)}
        </div>
        <div style={{ fontSize: "0.7rem", color: "rgba(160,160,200,0.5)" }}>
          pagamento único · {formatBRL(Math.round(pkg.priceCents / pkg.credits * 100) / 100)}/crédito
          — R$ {pricePerCredit} each
        </div>
      </div>

      {/* Description */}
      <p style={{ fontSize: "0.8rem", color: "rgba(160,160,200,0.7)", lineHeight: 1.6, margin: 0 }}>
        {pkg.description}
      </p>

      {/* Features */}
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
        {[
          `${pkg.credits} gerações 3D completas`,
          "GLB + STL reparado inclusos",
          "Visualizador 3D interativo",
          "Download imediato",
          pkg.credits >= 200 ? "Acesso à API B2B" : null,
          pkg.credits >= 500 ? "Suporte prioritário" : null,
        ].filter(Boolean).map((f) => (
          <li key={f} style={{ fontSize: "0.75rem", color: "rgba(160,160,200,0.7)", display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ color: "#6ee7b7", fontSize: "0.7rem" }}>✓</span> {f}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        id={`billing-buy-${pkg.id}`}
        onClick={() => onSelect(pkg.id)}
        disabled={!!loading}
        style={{
          padding: "0.875rem",
          borderRadius: 12,
          border: "none",
          cursor: loading ? "not-allowed" : "pointer",
          background: isLoading
            ? "rgba(124,58,237,0.3)"
            : pkg.popular
            ? "linear-gradient(135deg, #7c3aed, #3b82f6)"
            : "rgba(124,58,237,0.15)",
          color: pkg.popular || isLoading ? "white" : "#a78bfa",
          fontWeight: 700,
          fontSize: "0.95rem",
          boxShadow: pkg.popular && !loading ? "0 4px 24px rgba(124,58,237,0.4)" : "none",
          transition: "all 0.3s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
          marginTop: "auto",
          border: pkg.popular ? "none" : "1px solid rgba(124,58,237,0.25)",
        } as React.CSSProperties}
      >
        {isLoading ? (
          <>
            <span style={{
              display: "inline-block", width: 14, height: 14,
              border: "2px solid rgba(255,255,255,0.3)",
              borderTop: "2px solid white", borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }} />
            Redirecionando…
          </>
        ) : (
          `Comprar ${pkg.name} →`
        )}
      </button>
    </div>
  );
}

// ── Main Billing Page ─────────────────────────────────────────────────────

export default function BillingPage() {
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [stripeReady, setStripeReady] = useState(true);

  // Busca créditos atuais
  useEffect(() => {
    fetch("/api/user/me")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setCredits(data.credits); })
      .catch(() => {});
  }, []);

  const handleSelect = async (packageId: string) => {
    setLoading(packageId);
    try {
      const res = await fetch("/api/v1/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packageId }),
      });

      const data = await res.json() as { url?: string; error?: string };

      if (!res.ok || !data.url) {
        if (data.error?.includes("não configurado") || data.error?.includes("not configured")) {
          setStripeReady(false);
        }
        alert(data.error ?? "Erro ao iniciar checkout");
        setLoading(null);
        return;
      }

      window.location.href = data.url;
    } catch {
      alert("Erro de rede");
      setLoading(null);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "radial-gradient(ellipse 80% 40% at 50% -10%, rgba(124,58,237,0.25) 0%, transparent 60%), rgb(8,8,18)",
        color: "#f0f0ff",
        fontFamily: "var(--font-inter, system-ui, sans-serif)",
      }}
    >
      {/* Navbar */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 50,
        background: "rgba(8,8,18,0.9)", backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        padding: "0.875rem 1.5rem",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", gap: "0.625rem", textDecoration: "none" }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg, #7c3aed, #3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
            ◈
          </div>
          <span style={{ fontWeight: 800, fontSize: "1rem", background: "linear-gradient(135deg, #a78bfa, #60a5fa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            3DMaker Club
          </span>
        </a>

        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {credits !== null && (
            <div style={{
              background: credits > 0 ? "rgba(52,211,153,0.1)" : "rgba(239,68,68,0.1)",
              border: `1px solid ${credits > 0 ? "rgba(52,211,153,0.25)" : "rgba(239,68,68,0.25)"}`,
              color: credits > 0 ? "#6ee7b7" : "#fca5a5",
              borderRadius: 8, padding: "0.25rem 0.75rem",
              fontSize: "0.8rem", fontWeight: 700,
            }}>
              ✦ {credits} créditos
            </div>
          )}
          <a href="/dashboard" style={{ color: "rgba(160,160,200,0.6)", fontSize: "0.8rem", textDecoration: "none" }}>Dashboard</a>
          <a href="/studio" style={{
            background: "linear-gradient(135deg, #7c3aed, #3b82f6)",
            color: "white", borderRadius: 8, padding: "0.4rem 1rem",
            fontSize: "0.8rem", fontWeight: 700, textDecoration: "none",
          }}>
            Studio
          </a>
        </div>
      </nav>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "3rem 1.5rem" }}>

        {/* Stripe not configured warning */}
        {!stripeReady && (
          <div style={{
            background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: 12, padding: "1rem 1.5rem", marginBottom: "2rem",
            color: "#fca5a5", fontSize: "0.85rem",
          }}>
            <strong>⚠️ Stripe não configurado.</strong> Defina{" "}
            <code style={{ background: "rgba(0,0,0,0.3)", padding: "0.1rem 0.3rem", borderRadius: 4 }}>STRIPE_SECRET_KEY</code>{" "}
            e os Price IDs no <code>.env</code> para ativar os pagamentos.
          </div>
        )}

        {/* Success/Cancel */}
        <Suspense>
          <StatusBanner />
        </Suspense>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "3.5rem" }}>
          <div style={{
            display: "inline-block",
            background: "rgba(124,58,237,0.1)",
            border: "1px solid rgba(124,58,237,0.2)",
            borderRadius: 20,
            padding: "0.3rem 1rem",
            fontSize: "0.75rem",
            color: "#a78bfa",
            fontWeight: 600,
            letterSpacing: "0.05em",
            marginBottom: "1.5rem",
          }}>
            💳 Recarga de Créditos
          </div>

          <h1 style={{
            fontSize: "clamp(2rem, 4vw, 3rem)",
            fontWeight: 900,
            letterSpacing: "-0.03em",
            margin: "0 0 1rem",
            lineHeight: 1.1,
          }}>
            Escolha seu{" "}
            <span style={{ background: "linear-gradient(135deg, #a78bfa, #60a5fa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              pacote de créditos
            </span>
          </h1>

          <p style={{ color: "rgba(160,160,200,0.7)", maxWidth: 500, margin: "0 auto", lineHeight: 1.6, fontSize: "0.95rem" }}>
            Cada crédito = 1 modelo 3D completo (GLB + STL reparado). Sem assinatura, sem renovação automática.
          </p>

          {credits !== null && (
            <div style={{ marginTop: "1rem", fontSize: "0.85rem", color: "rgba(160,160,200,0.5)" }}>
              Saldo atual: <strong style={{ color: "#a78bfa" }}>{credits} créditos</strong>
            </div>
          )}
        </div>

        {/* Cards grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "1.5rem",
          alignItems: "start",
        }}>
          {CREDIT_PACKAGES.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              onSelect={handleSelect}
              loading={loading}
            />
          ))}
        </div>

        {/* FAQ / Notes */}
        <div style={{
          marginTop: "4rem",
          padding: "2rem",
          background: "rgba(16,16,32,0.5)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "1.25rem",
        }}>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: "1.25rem", letterSpacing: "-0.01em" }}>
            Dúvidas frequentes
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.5rem" }}>
            {[
              { q: "Os créditos expiram?", a: "Não. Os créditos 3DMaker Club não têm prazo de validade." },
              { q: "Posso usar na API B2B?", a: "Sim. Cada chamada à API consome 1 crédito, igual ao Studio." },
              { q: "O pagamento é seguro?", a: "Sim. Processado pelo Stripe com PCI-DSS Level 1." },
              { q: "Posso pedir reembolso?", a: "Sim, dentro de 7 dias se nenhum crédito foi utilizado." },
            ].map(({ q, a }) => (
              <div key={q}>
                <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.375rem" }}>{q}</div>
                <div style={{ fontSize: "0.8rem", color: "rgba(160,160,200,0.6)", lineHeight: 1.5 }}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
